import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
	ActivatedRoute,
	NavigationEnd,
	Router,
	RouterLink,
	RouterOutlet
} from '@angular/router';
import { AppolowUcfirstPipe } from '@appolow/form';
import { Translations } from '@core/services';
import { TranslocoPipe } from '@jsverse/transloco';
import { TabsModule } from 'primeng/tabs';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { filter, map } from 'rxjs';
import { DeployAppComponent } from '../../../deploy-app/deploy-app.component';

type DeploymentTab = {
	label: string;
	route: string;
	requiresApp?: boolean;
};

@Component({
	selector: 'app-deployments-and-instances',
	imports: [
		TabsModule,
		ButtonModule,
		RouterLink,
		RouterOutlet,
		TranslocoPipe,
		AppolowUcfirstPipe
	],
	templateUrl: './deployments-and-instances.component.html',
	providers: [DialogService],
	host: {
		class: 'detail-page detail-page--container'
	}
})
export class DeploymentsAndInstancesComponent {
	#activatedRoute = inject(ActivatedRoute);
	#router = inject(Router);
	#dialog = inject(DialogService);

	app = toSignal(this.#activatedRoute.data.pipe(map(({ app = null }) => app)));

	readonly tabChildren: DeploymentTab[] = [
		{
			label: 'DEPLOYMENTS_AND_INSTANCES.TITLES.INSTANCES',
			route: 'app-instances'
		},
		{
			label: 'DEPLOYMENTS_AND_INSTANCES.TITLES.VERSIONS',
			route: 'versions',
			requiresApp: true
		},
		{
			label: 'DEPLOYMENTS_AND_INSTANCES.TITLES.DEPLOYMENTS',
			route: 'history'
		}
	];

	filteredTabs = computed(() =>
		this.tabChildren.filter((tab) => !tab.requiresApp || this.app())
	);

	readonly #url = toSignal(
		this.#router.events.pipe(
			filter((event) => event instanceof NavigationEnd),
			map(() => this.#router.url)
		),
		{ initialValue: this.#router.url }
	);

	protected readonly activeTab = computed(() => {
		const segments = this.#url().split(/[?#]/)[0].split('/');
		return this.tabChildren.find((tab) => segments.includes(tab.route))?.route;
	});

	/**
	 * Opens the deployment wizard modal for the current application.
	 *
	 * This method uses the modal service to open the `DeployAppComponent` in a modal dialog.
	 * The modal is configured with the current application's ID, a large size, animation enabled,
	 * and a static backdrop to prevent closing by clicking outside.
	 *
	 * @async
	 * @returns {Promise<void>} A promise that resolves when the modal is opened.
	 */
	showDeployWizard(): void {
		this.#dialog.open(DeployAppComponent, {
			header: Translations.instant('DEPLOY_APP.TITLES.FRIENDLY_DEPLOY', {
				name: this.app().name
			}),
			modal: true,
			closable: true,
			width: '48rem',
			breakpoints: { '960px': '95vw' },
			data: { applicationId: this.app().id }
		});
	}
}
